import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { analyzeTicker } from "../lib/api";
import { toast } from "sonner";

export default function AddTickerDialog({ ipoName, onAdd }) {
  const [open, setOpen] = useState(false);
  const [ticker, setTicker] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runAnalysis = async () => {
    const t = ticker.trim().toUpperCase().replace(/^\$/, "");
    if (!t) return;
    setLoading(true); setResult(null);
    try {
      const r = await analyzeTicker({ ticker: t, ipo_name: ipoName });
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const confirmAdd = () => {
    if (!result) return;
    onAdd({
      tier: result.suggested_tier,
      chip: { ticker: result.ticker, company: result.company, one_liner: result.one_liner },
    });
    toast.success(`Added $${result.ticker} to tier ${result.suggested_tier}`);
    setOpen(false); setTicker(""); setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setTicker(""); } }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          data-testid="add-ticker-button"
        >
          <Plus className="mr-2 h-4 w-4" /> Add ticker (AI analyzes fit)
        </Button>
      </DialogTrigger>
      <DialogContent className="border-white/10 bg-[#09090B] text-white sm:max-w-md" data-testid="add-ticker-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo text-2xl font-black tracking-tighter">Add a ticker</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-400">
          Enter any US public ticker. AI will analyze its fit as a proxy for{" "}
          <span className="font-mono-jb text-white">{ipoName}</span> and slot it into the right tier.
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="e.g. NVDA"
            value={ticker}
            onChange={(e) => setTicker(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runAnalysis()}
            className="border-white/15 bg-white/5 font-mono-jb text-white placeholder:text-zinc-600"
            data-testid="add-ticker-input"
          />
          <Button onClick={runAnalysis} disabled={loading || !ticker.trim()} data-testid="add-ticker-analyze">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          </Button>
        </div>

        {result && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="add-ticker-result">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-chivo text-xl font-bold">${result.ticker}</div>
                <div className="text-xs text-zinc-400">{result.company}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-jb text-[10px] uppercase tracking-[0.18em] text-zinc-500">Suggested tier</div>
                <div className="font-chivo text-2xl font-black tracking-tighter">{result.suggested_tier}</div>
              </div>
            </div>
            <p className="mt-3 text-sm italic text-zinc-300">“{result.one_liner}”</p>
            <p className="mt-2 text-xs leading-relaxed text-zinc-400">{result.reasoning}</p>
            <div className="mt-2 font-mono-jb text-[11px] text-zinc-500">Fit score: {result.fit_score}/100</div>
            <Button onClick={confirmAdd} className="mt-4 w-full bg-white text-black hover:bg-zinc-200" data-testid="add-ticker-confirm">
              Add to tier list
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
